import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Trash2, UserPlus, Upload, Download, X, Loader2, Search } from "lucide-react";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import SecurityBanner from "../components/SecurityBanner";
import PendingApprovalBanner from "../components/PendingApprovalBanner";
import { useAuth } from "../lib/auth";
import { calStage } from "../lib/calibrationStage";
import GettingStartedChecklist from "../components/GettingStartedChecklist";
import { Compass } from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const csvInputRef = React.useRef(null);
  // Filter + search state (both purely client-side over the same list).
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("__all__");
  const guideKey = `calibrate_guide_hidden_${user?.id}`;
  const [guideHidden, setGuideHidden] = useState(() => localStorage.getItem(guideKey) === "1");
  const toggleGuide = (hide) => {
    if (hide) localStorage.setItem(guideKey, "1"); else localStorage.removeItem(guideKey);
    setGuideHidden(hide);
  };
  const location = useLocation();
  const wantGuide = !!location.state?.showGuide;
  useEffect(() => {
    if (!wantGuide) return;
    localStorage.removeItem(guideKey);
    setGuideHidden(false);
    window.history.replaceState({}, "");
  }, [wantGuide, guideKey]);
  useEffect(() => {
    if (wantGuide && !loading) {
      const t = setTimeout(() => document.querySelector('[data-testid="onboarding-checklist"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);
      return () => clearTimeout(t);
    }
  }, [wantGuide, loading]);

  const reloadProfiles = React.useCallback(() => {
    setLoading(true);
    api.get("/profiles")
      .then((r) => setProfiles(r.data))
      .catch((e) => toast.error(apiError(e, "Failed to load")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user?.is_authorized) { setLoading(false); return; }
    reloadProfiles();
  }, [user, reloadProfiles]);

  const downloadCsvTemplate = async () => {
    try {
      const r = await api.get("/profiles/import-csv/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "employees-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(apiError(err, "Failed to download template"));
    }
  };

  const submitCsvImport = async () => {
    if (!csvFile) { toast.error("Select a CSV file"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("csv_file", csvFile);
      const r = await api.post("/profiles/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.created.length} profile${r.data.created.length === 1 ? "" : "s"}`);
      reloadProfiles();
    } catch (err) {
      toast.error(apiError(err, "CSV import failed"));
    } finally {
      setImporting(false);
    }
  };

  const closeImport = () => {
    setShowImport(false);
    setCsvFile(null);
    setImportResult(null);
    setImporting(false);
  };

  const target = profiles.find((p) => p.id === confirmId);

  // Department options (with per-department profile counts). Case-insensitive
  // grouping — first-seen casing wins so "Engineering" beats "engineering".
  // Profiles with no department fall into an explicit "Uncategorised" bucket.
  const departmentOptions = React.useMemo(() => {
    const byKey = new Map();
    let uncategorisedCount = 0;
    for (const p of profiles) {
      const raw = (p.department || "").trim();
      if (!raw) { uncategorisedCount++; continue; }
      const key = raw.toLowerCase();
      const existing = byKey.get(key);
      if (existing) existing.count += 1;
      else byKey.set(key, { value: key, label: raw, count: 1 });
    }
    const list = Array.from(byKey.values()).sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    if (uncategorisedCount > 0) {
      list.push({ value: "__uncategorised__", label: "Uncategorised", count: uncategorisedCount });
    }
    return list;
  }, [profiles]);

  const filteredProfiles = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles.filter((p) => {
      // Department filter
      if (deptFilter !== "__all__") {
        const dept = (p.department || "").trim().toLowerCase();
        if (deptFilter === "__uncategorised__") {
          if (dept) return false;
        } else if (dept !== deptFilter) {
          return false;
        }
      }
      // Search across name, email, job title, and department (per user pick).
      if (q) {
        const hay = [p.employee_name, p.employee_email, p.employee_title, p.department]
          .map((x) => (x || "").toLowerCase()).join(" \n ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [profiles, search, deptFilter]);


  const doDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await api.delete(`/profiles/${confirmId}`);
      setProfiles((ps) => ps.filter((p) => p.id !== confirmId));
      toast.success("Profile deleted");
      setConfirmId(null);
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <SecurityBanner />
      {user && !user.is_authorized && <PendingApprovalBanner orgName={user.org_name} />}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666]">[MANAGER] Dashboard</div>
          <h1 className="font-display font-bold text-5xl tracking-tight mt-2">Your team</h1>
          <p className="text-[#666] mt-2">Each profile has one Position Description and many calibrations over time.</p>
        </div>
        {user?.is_authorized && (
          <div className="flex flex-wrap items-center gap-2">
            {guideHidden && (
              <button onClick={() => toggleGuide(false)} className="cal-btn cal-btn-ghost" data-testid="show-guide-btn">
                <Compass size={14} /> Getting started
              </button>
            )}
            <button onClick={() => setShowImport(true)} className="cal-btn cal-btn-ghost" data-testid="import-csv-btn">
              <Upload size={14} /> Import CSV
            </button>
            <Link to="/profiles/new" className="cal-btn" data-testid="new-profile-btn"><UserPlus size={14} /> New Employee Profile</Link>
          </div>
        )}
      </div>

      {user?.is_authorized && !loading && !guideHidden && (
        <GettingStartedChecklist profiles={profiles} onDismiss={() => toggleGuide(true)} />
      )}

      {user?.is_authorized && profiles.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="dashboard-controls">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, title, or department…"
              className="cal-input !pl-8"
              data-testid="employee-search-input"
              aria-label="Search employees"
            />
          </div>
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            className="cal-input !w-auto min-w-[180px]"
            data-testid="department-filter"
            aria-label="Filter by department"
          >
            <option value="__all__">All departments</option>
            {departmentOptions.map((d) => (
              <option key={d.value} value={d.value}>{d.label} ({d.count})</option>
            ))}
          </select>
          {(search || deptFilter !== "__all__") && (
            <button
              type="button"
              onClick={() => { setSearch(""); setDeptFilter("__all__"); }}
              className="cal-btn cal-btn-ghost text-xs"
              data-testid="dashboard-filters-clear"
            >
              <X size={12} className="inline mr-1" /> Clear
            </button>
          )}
          <span className="font-mono-cal text-[11px] text-[#666]" data-testid="filtered-count">
            {filteredProfiles.length} of {profiles.length}
          </span>
        </div>
      )}

      {loading ? (
        <div className="font-mono-cal text-xs text-[#666]" data-testid="loading">[LOADING…]</div>
      ) : profiles.length === 0 ? (
        <div className="cal-card p-12 text-center" data-testid="empty-state">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[ EMPTY ]</div>
          <h3 className="font-display font-bold text-2xl mb-3">No employees yet</h3>
          <p className="text-[#666] mb-6">Create your first profile by uploading a Position Description.</p>
          <Link to="/profiles/new" className="cal-btn" data-testid="empty-new-btn">+ Create first profile</Link>
        </div>
      ) : filteredProfiles.length === 0 ? (
        <div className="cal-card p-10 text-center" data-testid="no-matches">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[ NO MATCHES ]</div>
          <div className="text-sm text-[#666]">No employees match your current filters.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProfiles.map((p) => (
            <div key={p.id} className="cal-card cal-shadow p-6 flex flex-col" data-testid={`profile-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">
                  {p.calibration_count} CALIBRATION{p.calibration_count === 1 ? "" : "S"}
                </div>
                <button onClick={() => setConfirmId(p.id)} className="p-1.5 border border-[#111] hover:bg-[#111] hover:text-white transition" title="Delete profile" data-testid={`delete-profile-${p.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              <button onClick={() => nav(`/profiles/${p.id}`)} className="text-left mt-3 flex-1" data-testid={`open-profile-${p.id}`}>
                <div className="font-display font-bold text-2xl leading-tight">{p.employee_name}</div>
                <div className="text-sm text-[#666] mt-1">{p.employee_title || "—"}</div>
                {p.department ? (
                  <div className="mt-1">
                    <span
                      className="inline-block px-1.5 py-0.5 border border-[#111]/40 text-[10px] font-mono-cal uppercase tracking-widest text-[#111]"
                      data-testid={`department-tag-${p.id}`}
                    >
                      {p.department}
                    </span>
                  </div>
                ) : null}
                <div className="font-mono-cal text-xs text-[#666] mt-3">{p.employee_email}</div>
                {/* Latest calibration summary — replaces the previous static
                    "N tasks from PD" line. Shows the title + a color-coded
                    status pip so managers can eyeball where each report is at
                    without opening the profile. */}
                <div className="mt-4 min-h-[44px]" data-testid={`latest-calibration-${p.id}`}>
                  {p.latest_calibration_title ? (
                    <>
                      <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[LATEST CALIBRATION]</div>
                      <div className="mt-1 flex items-center gap-2 text-sm">
                        <span
                          className={
                            "inline-block w-1.5 h-1.5 " +
                            (p.latest_calibration_status === "Complete"
                              ? "bg-[#00A36C]"
                              : p.latest_calibration_status === "In progress"
                              ? "bg-[#FF4F00]"
                              : "bg-[#999]")
                          }
                          aria-hidden="true"
                        />
                        <span className="font-medium truncate" data-testid={`latest-calibration-title-${p.id}`}>{p.latest_calibration_title}</span>
                        <span
                          className={`ml-auto shrink-0 font-mono-cal text-[10px] uppercase tracking-widest ${p.latest_calibration_status === "Complete" ? "text-[#00A36C]" : "text-[#FF4F00]"}`}
                          data-testid={`latest-calibration-status-${p.id}`}
                        >
                          {calStage({ employee_submitted: p.latest_calibration_employee_submitted, manager_submitted: p.latest_calibration_manager_submitted, conversation_held_on: p.latest_calibration_conversation_held_on }).label}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-xs font-mono-cal text-[#666]">
                      <span className="inline-block w-1 h-1 bg-[#999]" />
                      NO CALIBRATIONS YET
                    </div>
                  )}
                </div>
              </button>
              <div className="mt-5 border-t border-[#111]/20 pt-4 flex items-center justify-between">
                <span
                  className={`font-mono-cal text-[10px] uppercase ${p.pd_filename ? "text-[#666]" : "text-[#B00020]"}`}
                  data-testid={`pd-filename-${p.id}`}
                >
                  {p.pd_filename ? <>PD · {p.pd_filename.slice(0, 24)}…</> : <>NO PD ATTACHED</>}
                </span>
                <button onClick={() => nav(`/profiles/${p.id}`)} className="font-mono-cal text-xs text-[#FF4F00] hover:underline" data-testid={`view-profile-${p.id}`}>OPEN →</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmId && (
        <ConfirmDeleteModal
          open={true}
          title="Delete profile?"
          subjectLabel="This removes"
          subjectName={target?.employee_name}
          warning="Every calibration ever run for this employee will also be destroyed. This cannot be undone."
          requiredWord="DELETE"
          confirmLabel="Delete profile"
          onCancel={() => setConfirmId(null)}
          onConfirm={doDelete}
          busy={deleting}
        />
      )}

      {showImport && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
          data-testid="import-csv-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !importing) closeImport(); }}
        >
          <div className="bg-white w-full max-w-2xl my-8 border-2 border-[#111] shadow-xl">
            <div className="p-5 border-b border-[#EEE] flex items-start justify-between gap-3">
              <div>
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ BULK IMPORT ]</div>
                <h2 className="font-display font-bold text-2xl mt-1">Import employees from CSV</h2>
                <p className="text-[#666] text-sm mt-1">
                  Columns required: <span className="font-mono-cal">employee_name, employee_email, employee_title</span>.
                  Each row creates a profile without a PD — attach one from the profile page after import.
                </p>
              </div>
              <button onClick={closeImport} disabled={importing} className="cal-btn cal-btn-ghost" data-testid="import-csv-close" aria-label="Close">
                <X size={14} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <button type="button" onClick={downloadCsvTemplate} className="cal-btn cal-btn-ghost" data-testid="import-csv-template-btn">
                  <Download size={14} /> Download CSV template
                </button>
              </div>
              <div
                onClick={() => !importing && csvInputRef.current?.click()}
                className={`border border-dashed border-[#111] p-8 text-center cursor-pointer transition ${importing ? "pointer-events-none opacity-60" : "hover:bg-[#F4F4F0]"}`}
                data-testid="import-csv-dropzone"
              >
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="hidden"
                  data-testid="import-csv-file-input"
                />
                <div className="font-mono-cal text-[11px] uppercase tracking-widest text-[#666] mb-2">[ CSV ]</div>
                <div className="font-display font-bold text-lg">{csvFile ? csvFile.name : "Click to select a CSV file"}</div>
                {csvFile && <div className="mt-1 text-xs text-[#666]">{(csvFile.size / 1024).toFixed(1)} KB</div>}
              </div>

              {importResult && (
                <div className="cal-card p-4 bg-[#F4F4F0]" data-testid="import-csv-result">
                  <div className="font-display font-semibold text-sm mb-2">
                    <span className="text-[#0B7A3B]">{importResult.created.length} created</span>
                    {" · "}
                    <span className="text-[#B00020]">{importResult.skipped.length} skipped</span>
                  </div>
                  {importResult.skipped.length > 0 && (
                    <ul className="text-xs font-mono-cal text-[#666] max-h-40 overflow-y-auto" data-testid="import-csv-skipped-list">
                      {importResult.skipped.map((s, i) => (
                        <li key={i}>Row {s.row}: {s.reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-[#EEE] flex justify-end gap-2">
              <button
                type="button"
                onClick={closeImport}
                disabled={importing}
                className="cal-btn cal-btn-ghost"
                data-testid="import-csv-cancel"
              >
                {importResult ? "Close" : "Cancel"}
              </button>
              {!importResult && (
                <button
                  type="button"
                  onClick={submitCsvImport}
                  disabled={importing || !csvFile}
                  className="cal-btn"
                  data-testid="import-csv-submit"
                >
                  {importing ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Importing…</> : <><Upload size={14} className="inline mr-1" /> Import</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
